import { Briefcase, ChevronRight, Loader2, MapPin, MapPinPlus, Pencil, Target, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { CheckRow, Field, FormSection } from '@/components/common/FormBits'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Kpi } from '@/features/finance/components/FinanceBits'
import { toastError } from '@/features/finance/ui'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtMoney } from '../shared/format'
import { useRegions, useSaveRegion } from './api'
import { Dot, Toolbar } from './components/AdminBits'
import type { Region, RegionInput } from './types'
import { includesTr } from './ui'

const pct = (actual: string, target: string) => (Number(target) > 0 ? Math.round((Number(actual) / Number(target)) * 100) : 0)

export function RegionsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const list = useRegions()
  const [q, setQ] = useState('')
  const [form, setForm] = useState<{ open: boolean; region: Region | null }>({ open: false, region: null })
  const canWrite = can('settings.write')
  const all = list.data ?? []
  const rows = all.filter((r) => includesTr(`${r.code} ${r.name} ${r.manager} ${r.cities.join(' ')}`, q))
  const active = all.filter((r) => r.active)
  const target = active.reduce((n, r) => n + Number(r.quarterTarget), 0)
  const actual = active.reduce((n, r) => n + Number(r.quarterActual), 0)

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.admin')}
        title={t('admin.regions.title')}
        description={t('admin.regions.subtitle')}
        actions={
          canWrite ? (
            <Button onClick={() => setForm({ open: true, region: null })}>
              <MapPinPlus />
              {t('admin.regions.new')}
            </Button>
          ) : null
        }
      />

      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={MapPin} tone="primary" label={t('admin.regions.kpi.active')} value={active.length} footer={t('admin.regions.kpi.activeHint', { count: active.reduce((n, r) => n + r.cities.length, 0) })} />
            <Kpi icon={Users} tone="info" label={t('admin.regions.kpi.customers')} value={all.reduce((n, r) => n + r.customers, 0)} footer={t('admin.regions.kpi.customersHint', { count: all.reduce((n, r) => n + r.liveCustomers, 0) })} />
            <Kpi icon={Briefcase} tone="warning" label={t('admin.regions.kpi.pipeline')} value={fmtMoney(all.reduce((n, r) => n + Number(r.pipelineValue), 0).toFixed(2))} footer={t('admin.regions.kpi.pipelineHint', { count: all.reduce((n, r) => n + r.openDeals, 0) })} />
            <Kpi icon={Target} tone="success" label={t('admin.regions.kpi.target')} value={`%${pct(String(actual), String(target))}`} footer={t('admin.regions.kpi.targetHint', { actual: fmtMoney(actual.toFixed(2)), target: fmtMoney(target.toFixed(2)) })} />
          </div>

          <section className="overflow-hidden rounded-2xl border bg-card shadow-xs">
            <Toolbar q={q} onQ={setQ} placeholder={t('admin.regions.search')} count={t('admin.common.count', { count: rows.length })} />
            {rows.length === 0 ? (
              <EmptyState icon={MapPin} title={all.length ? t('admin.common.noMatch') : t('admin.regions.empty')} />
            ) : (
              <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-2 2xl:grid-cols-3">
                {rows.map((r) => {
                  const p = pct(r.quarterActual, r.quarterTarget)
                  return (
                    <article key={r.id} className={cn('flex flex-col gap-3 rounded-xl border p-4', !r.active && 'bg-muted/40')}>
                      <header className="flex items-start gap-2">
                        <Dot tone={r.tone} className="mt-1.5 size-2.5" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold">{r.name}</h3>
                            <span className="font-mono text-xs text-muted-foreground">{r.code}</span>
                            {!r.active ? <Badge>{t('admin.regions.inactive')}</Badge> : null}
                          </div>
                          <p className="text-xs text-muted-foreground">{t('admin.regions.manager', { name: r.manager })}</p>
                        </div>
                        {canWrite ? (
                          <Button size="icon" variant="ghost" className="size-8" aria-label={t('admin.regions.editFor', { name: r.name })} onClick={() => setForm({ open: true, region: r })}>
                            <Pencil />
                          </Button>
                        ) : null}
                      </header>
                      <div className="flex flex-wrap gap-1.5">
                        {r.cities.map((c) => (
                          <Badge key={c} tone="outline">
                            {c}
                          </Badge>
                        ))}
                      </div>
                      <dl className="grid grid-cols-3 gap-2 rounded-lg bg-muted/50 p-2.5 text-center text-xs">
                        <div>
                          <dt className="text-muted-foreground">{t('admin.regions.customers')}</dt>
                          <dd className="text-base font-semibold tabular-nums">{r.customers}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">{t('admin.regions.live')}</dt>
                          <dd className="text-base font-semibold tabular-nums">{r.liveCustomers}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">{t('admin.regions.deals')}</dt>
                          <dd className="text-base font-semibold tabular-nums">{r.openDeals}</dd>
                        </div>
                      </dl>
                      {Number(r.quarterTarget) > 0 ? (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{t('admin.regions.quarter')}</span>
                            <span className="font-medium tabular-nums">
                              {fmtMoney(r.quarterActual)} / {fmtMoney(r.quarterTarget)}
                            </span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-muted" role="meter" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100} aria-label={t('admin.regions.quarter')}>
                            <div className={cn('h-full rounded-full', p >= 80 ? 'bg-success' : p >= 50 ? 'bg-warning' : 'bg-destructive')} style={{ width: `${Math.min(100, p)}%` }} />
                          </div>
                          <p className="text-right text-[11px] tabular-nums text-muted-foreground">%{p}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">{t('admin.regions.noTarget')}</p>
                      )}
                      <div className="mt-auto flex items-center justify-between gap-2 border-t pt-3 text-xs">
                        <span className="text-muted-foreground">{t('admin.regions.pipeline', { value: fmtMoney(r.pipelineValue) })}</span>
                        <Link to={`/customers/list?region=${encodeURIComponent(r.name)}`} className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline">
                          {t('admin.regions.showCustomers')}
                          <ChevronRight className="size-3.5" aria-hidden />
                        </Link>
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </section>
        </div>
      )}

      {form.open ? <RegionForm region={form.region} onDone={() => setForm({ open: false, region: null })} /> : null}
    </>
  )
}

function RegionForm({ region, onDone }: { region: Region | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveRegion()
  const [f, setF] = useState<RegionInput>(
    region
      ? { code: region.code, name: region.name, cities: region.cities, manager: region.manager, quarterTarget: region.quarterTarget, active: region.active }
      : { code: '', name: '', cities: [], manager: '', quarterTarget: '0.00', active: true },
  )
  const [cities, setCities] = useState(f.cities.join(', '))
  const [target, setTarget] = useState(String(Number(f.quarterTarget)))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const n = Number(target.replace(/\./g, '').replace(',', '.'))
    if (!Number.isFinite(n) || n < 0) {
      toast.error(t('admin.regions.invalidTarget'))
      return
    }
    save.mutate(
      { id: region?.id, input: { ...f, code: f.code.trim().toLocaleUpperCase('tr'), name: f.name.trim(), cities: cities.split(',').map((c) => c.trim()).filter(Boolean), quarterTarget: n.toFixed(2) } },
      {
        onSuccess: () => {
          toast.success(t('common.saved'))
          onDone()
        },
        onError: toastError,
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={region ? <MapPin /> : <MapPinPlus />}
      title={region ? t('admin.regions.edit') : t('admin.regions.new')}
      onSubmit={submit}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="rg-code" label={t('admin.regions.code')}>
          <Input id="rg-code" value={f.code} onChange={(e) => setF((p) => ({ ...p, code: e.target.value }))} required maxLength={8} className="font-mono uppercase" />
        </Field>
        <Field id="rg-name" label={t('admin.regions.name')}>
          <Input id="rg-name" value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} required />
        </Field>
        <Field id="rg-cities" label={t('admin.regions.cities')} hint={t('admin.regions.citiesHint')} full>
          <Input id="rg-cities" value={cities} onChange={(e) => setCities(e.target.value)} />
        </Field>
        <Field id="rg-manager" label={t('admin.regions.managerLabel')}>
          <Input id="rg-manager" value={f.manager} onChange={(e) => setF((p) => ({ ...p, manager: e.target.value }))} />
        </Field>
        <Field id="rg-target" label={t('admin.regions.targetLabel')}>
          <Input id="rg-target" value={target} onChange={(e) => setTarget(e.target.value)} inputMode="decimal" />
        </Field>
        <CheckRow checked={f.active} onChange={(e) => setF((p) => ({ ...p, active: e.target.checked }))} hint={t('admin.regions.activeHint')}>
          {t('admin.regions.activeLabel')}
        </CheckRow>
      </FormSection>
    </Modal>
  )
}
